import {
  DashboardFiltersState,
  DashboardSection,
  CategorizedDashboardTasks,
} from './dashboardTypes';
import { FormattedTask } from './taskTypes';
import { formatDeadline } from '../../lib/project-utils';

// Helper functions for working with FormattedTask
// These replace the functions we were importing from dashboard/utils/dashboardUtils.ts

/**
 * Determines if a task is overdue based on deadline
 */
const isFormattedTaskOverdue = (task: FormattedTask): boolean => {
  if (!task.deadline || task.status === 'done') return false;

  // Use formatDeadline utility
  const deadlineInfo = formatDeadline(task.deadline);
  if (!deadlineInfo) return false;

  // Double-check if today directly
  const dateObj = new Date(task.deadline);
  const today = new Date();
  const isSameDay =
    dateObj.getDate() === today.getDate() &&
    dateObj.getMonth() === today.getMonth() &&
    dateObj.getFullYear() === today.getFullYear();

  // Only include truly overdue tasks (past due, not today)
  return deadlineInfo.isOverdue && !deadlineInfo.isToday && !isSameDay;
};

/**
 * Determines if a task is due today
 */
const isFormattedTaskToday = (task: FormattedTask): boolean => {
  if (!task.deadline || task.status === 'done') return false;

  // Use formatDeadline utility
  const deadlineInfo = formatDeadline(task.deadline);
  if (!deadlineInfo) return false;

  // Double-check the date to ensure it's truly today
  const dateObj = new Date(task.deadline);
  const today = new Date();
  const isSameDay =
    dateObj.getDate() === today.getDate() &&
    dateObj.getMonth() === today.getMonth() &&
    dateObj.getFullYear() === today.getFullYear();

  // Use both deadlineInfo.isToday AND direct date comparison
  return deadlineInfo.isToday || isSameDay;
};

/**
 * Sorts FormattedTasks by deadline
 */
const sortFormattedTasksByDeadline = (tasks: FormattedTask[]): FormattedTask[] => {
  return [...tasks].sort((a, b) => {
    // If a task doesn't have a deadline, it goes to the bottom
    if (!a.deadline && !b.deadline) return 0;
    if (!a.deadline) return 1;
    if (!b.deadline) return -1;

    // Compare deadlines directly
    return a.deadline < b.deadline ? -1 : a.deadline > b.deadline ? 1 : 0;
  });
};

/**
 * Apply dashboard filters to FormattedTasks
 *
 * @param tasks - The formatted tasks to filter
 * @param filters - The filters to apply
 * @param currentUserId - The current user ID for filtering owned projects
 * @param projectIsSharedFunc - Function to determine if a project is shared
 * @returns Filtered tasks matching the criteria
 */
export const applyDashboardFilters = (
  tasks: FormattedTask[],
  filters: DashboardFiltersState,
  currentUserId: string | null,
  projectIsSharedFunc: (projectId: string) => boolean
): FormattedTask[] => {
  // Filter out completed tasks first
  let filteredTasks = tasks.filter(task => task.status !== 'done');

  const { userIds, projectIds, tags } = filters;

  // Apply user filters - IMPORTANT: Empty userIds means show ALL tasks (no user filtering)
  if (userIds.length > 0) {
    filteredTasks = filteredTasks.filter(task => {
      // Extract project ID from the task's topicId or topic field
      let projectId: string | undefined;
      if (task.topics) {
        const topicInfo = task.topics as { project_id: string; id: string };
        projectId = topicInfo.project_id;
      }

      // Special case: "Shared & unassigned" filter option
      if (userIds.includes('shared-unassigned')) {
        const isUnassigned = !task.assignedTo;
        const hasDueDate = !!task.deadline;

        if (isUnassigned && hasDueDate && projectId) {
          const isSharedProject = projectIsSharedFunc(projectId);
          if (isSharedProject) {
            return true;
          }
        }
      }

      // Task assigned to user in filter
      if (task.assignedTo && userIds.includes(task.assignedTo)) {
        return true;
      }

      // Task from non-shared project owned by current user
      if (projectId && currentUserId && userIds.includes(currentUserId)) {
        const isSharedProject = projectIsSharedFunc(projectId);
        if (!isSharedProject) {
          return true;
        }
      }

      return false;
    });
  }

  // Apply project filters
  if (projectIds.length > 0) {
    filteredTasks = filteredTasks.filter(task => {
      let projectId: string | undefined;
      if (task.topics) {
        const topicInfo = task.topics as { project_id: string; id: string };
        projectId = topicInfo.project_id;
      }
      return projectId && projectIds.includes(projectId);
    });
  }

  // Calculate available tags from filtered tasks AFTER user/project filtering
  // but BEFORE applying tag filtering
  const availableTagSet = new Set<string>();
  filteredTasks.forEach(task => {
    if (task.tags && Array.isArray(task.tags)) {
      task.tags.forEach(tag => availableTagSet.add(tag));
    }
  });

  // Apply tag filters
  if (tags.length > 0) {
    filteredTasks = filteredTasks.filter(task => {
      return task.tags && Array.isArray(task.tags) && task.tags.some(tag => tags.includes(tag));
    });
  }

  return filteredTasks;
};

/**
 * Categorize FormattedTasks into dashboard sections
 *
 * Each task can only appear in one section, with priority:
 * 1. Highlighted
 * 2. Overdue
 * 3. Today
 * 4. This Week
 *
 * @param tasks - The FormattedTask objects to categorize
 * @returns Object containing categorized and sorted tasks
 */
export const categorizeDashboardTasks = (tasks: FormattedTask[]): CategorizedDashboardTasks => {
  // First, filter out all completed tasks
  const incompleteTasks = tasks.filter(task => task.status !== 'done');

  // Then assign dashboard sections to remaining tasks based on priority
  const enhancedTasks = incompleteTasks.map(task => {
    let dashboardSection: DashboardSection;

    // Priority-based categorization
    if (task.isHighlighted) {
      dashboardSection = 'highlighted';
    } else if (isFormattedTaskOverdue(task)) {
      dashboardSection = 'overdue';
    } else if (isFormattedTaskToday(task)) {
      dashboardSection = 'today';
    } else {
      dashboardSection = 'thisWeek';
    }

    return { ...task, dashboardSection };
  });

  // Filter tasks into their respective categories and sort by deadline
  const highlightedTasks = sortFormattedTasksByDeadline(
    enhancedTasks.filter(task => task.dashboardSection === 'highlighted')
  ) as any[]; // Cast to avoid type errors with dashboard components

  const overdueTasks = sortFormattedTasksByDeadline(
    enhancedTasks.filter(task => task.dashboardSection === 'overdue')
  ) as any[];

  const todayTasks = sortFormattedTasksByDeadline(
    enhancedTasks.filter(task => task.dashboardSection === 'today')
  ) as any[];

  const thisWeekTasks = sortFormattedTasksByDeadline(
    enhancedTasks.filter(task => task.dashboardSection === 'thisWeek')
  ) as any[];

  // Calculate available tags from all filtered tasks
  const availableTagSet = new Set<string>();
  enhancedTasks.forEach(task => {
    if (task.tags && Array.isArray(task.tags)) {
      task.tags.forEach(tag => availableTagSet.add(tag));
    }
  });
  const availableTags = Array.from(availableTagSet).sort();

  return {
    highlightedTasks,
    overdueTasks,
    todayTasks,
    thisWeekTasks,
    availableTags,
  };
};
